import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { financeApi, type BalanceSheetStatement } from '@/api/financeApi';
import { formatINR } from '@/utils/format';

type PresetType = 'TODAY' | 'LAST_MONTH' | 'LAST_QUARTER' | 'LAST_FY';
type CompareMode = 'NONE' | 'PREV_QUARTER' | 'PREV_FY_END';

export default function BalanceSheetPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const today = new Date().toISOString().split('T')[0];
  const [asOfDate, setAsOfDate] = useState(today);
  const [preset, setPreset] = useState<PresetType>('TODAY');
  const [compareWith, setCompareWith] = useState<CompareMode>('NONE');

  const [loading, setLoading] = useState(true);
  const [bs, setBs] = useState<BalanceSheetStatement | null>(null);

  const applyPreset = (type: PresetType) => {
    setPreset(type);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    if (type === 'TODAY') {
      setAsOfDate(today);
    } else if (type === 'LAST_MONTH') {
      const lastMonthEnd = new Date(year, month, 0).toISOString().split('T')[0];
      setAsOfDate(lastMonthEnd);
    } else if (type === 'LAST_QUARTER') {
      const qStartMonth = Math.floor(month / 3) * 3;
      const lastQuarterEnd = new Date(year, qStartMonth, 0).toISOString().split('T')[0];
      setAsOfDate(lastQuarterEnd);
    } else if (type === 'LAST_FY') {
      const currentFyStart = month >= 3 ? year : year - 1;
      const lastFyEnd = `${currentFyStart}-03-31`;
      setAsOfDate(lastFyEnd);
    }
  };

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await financeApi.getBalanceSheet({ asOfDate, compare: compareWith });
      setBs(data);
    } catch (err: any) {
      console.error('Failed to load Balance Sheet:', err);
      toast.error(err.response?.data?.message || 'Failed to load Balance Sheet');
    } finally {
      setLoading(false);
    }
  }, [asOfDate, compareWith, toast]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    if (!bs) return;
    const rows: (string | number)[][] = [
      ['ASSETS', ''],
      ['Current Assets', ''],
      ...bs.assets.currentAssets.items.map((i) => [`  ${i.code} - ${i.name}`, i.amount, i.comparisonAmount ?? '', i.changePercent ? `${i.changePercent}%` : '']),
      ['Total Current Assets', bs.assets.currentAssets.total],
      ['Fixed Assets', ''],
      ...bs.assets.fixedAssets.items.map((i) => [`  ${i.code} - ${i.name}`, i.amount, i.comparisonAmount ?? '', i.changePercent ? `${i.changePercent}%` : '']),
      ['Total Fixed Assets', bs.assets.fixedAssets.total],
      ['TOTAL ASSETS', bs.assets.totalAssets],
      ['', ''],
      ['LIABILITIES & EQUITY', ''],
      ['Current Liabilities', ''],
      ...bs.liabilities.currentLiabilities.items.map((i) => [`  ${i.code} - ${i.name}`, i.amount, i.comparisonAmount ?? '', i.changePercent ? `${i.changePercent}%` : '']),
      ['Total Current Liabilities', bs.liabilities.currentLiabilities.total],
      ['Long Term Liabilities', ''],
      ...bs.liabilities.longTermLiabilities.items.map((i) => [`  ${i.code} - ${i.name}`, i.amount, i.comparisonAmount ?? '', i.changePercent ? `${i.changePercent}%` : '']),
      ['Total Long Term Liabilities', bs.liabilities.longTermLiabilities.total],
      ['Equity & Reserves', ''],
      ...bs.equity.items.map((i) => [`  ${i.code} - ${i.name}`, i.amount, i.comparisonAmount ?? '', i.changePercent ? `${i.changePercent}%` : '']),
      ['Total Equity', bs.equity.totalEquity],
      ['TOTAL LIABILITIES & EQUITY', bs.totalLiabilitiesAndEquity],
    ];

    const csv = [
      ['Particulars', 'Amount (INR)', 'Comparison Amount (INR)', 'Change %'].join(','),
      ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Balance_Sheet_${asOfDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Balance Sheet exported');
  };

  const renderChangeBadge = (changePercent?: number | null) => {
    if (changePercent === undefined || changePercent === null) return null;
    const isPositive = changePercent >= 0;
    return (
      <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${isPositive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
        <i className={isPositive ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
        {Math.abs(changePercent).toFixed(1)}%
      </span>
    );
  };

  return (
    <AppLayout>
      <div className="w-full flex flex-col min-h-screen bg-[#f8fafc] animate-fade-in">
        {/* Full Width Container */}
        <div className="w-full p-6 space-y-5">
          
          {/* Top Header */}
          <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-3 print:hidden">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-[#1e293b]">Balance Sheet</h1>
                {bs && (
                  <span
                    className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
                      bs.isBalanced
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        : 'bg-rose-50 text-rose-800 border-rose-200'
                    }`}
                  >
                    {bs.isBalanced ? 'Balanced Statement ✅' : `Imbalance: ${formatINR(bs.difference)} ⚠️`}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                Point-in-time financial snapshot showing company Assets, Liabilities, and Owner's Equity.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handlePrint}
                className="h-9 px-3.5 rounded-lg bg-white hover:bg-slate-50 border border-[#e2e8f0] text-sm font-medium text-slate-700 transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-printer-line text-sm text-slate-500" />
                <span>Print</span>
              </button>
              <button
                onClick={handleExport}
                className="h-9 px-3.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-download-2-line text-sm text-white" />
                <span>Export CSV</span>
              </button>
              <button
                onClick={() => navigate('/finance/trial-balance')}
                className="h-9 px-3.5 rounded-lg bg-[#4f46e5] hover:bg-indigo-700 text-white text-sm font-medium transition-colors flex items-center gap-1.5 cursor-pointer"
              >
                <i className="ri-scales-3-line text-sm text-white" />
                <span>Trial Balance</span>
              </button>
            </div>
          </div>

          {/* Controls Bar */}
          <div className="w-full bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 print:hidden">
            {/* Left: As of Date & Presets */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="flex items-center gap-1.5 bg-white border border-[#e2e8f0] rounded-lg px-2.5 h-9 text-sm text-slate-700">
                <span className="text-slate-400 font-medium">As On Date:</span>
                <input
                  type="date"
                  value={asOfDate}
                  onChange={(e) => {
                    setAsOfDate(e.target.value);
                    setPreset('TODAY');
                  }}
                  className="bg-transparent font-medium text-[#1e293b] focus:outline-none cursor-pointer"
                />
              </div>

              {/* Date Presets */}
              <div className="flex items-center gap-0.5 bg-slate-100 p-0.5 rounded-lg border border-[#e2e8f0] text-xs">
                {(
                  [
                    { id: 'TODAY', label: 'Today' },
                    { id: 'LAST_MONTH', label: 'Last Month End' },
                    { id: 'LAST_QUARTER', label: 'Last Quarter End' },
                    { id: 'LAST_FY', label: 'Last FY End' },
                  ] as const
                ).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => applyPreset(p.id)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer ${
                      preset === p.id
                        ? 'bg-[#4f46e5] text-white shadow-sm font-semibold'
                        : 'bg-transparent text-slate-600 hover:bg-slate-200/60'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Compare Selector */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="flex items-center gap-1.5 bg-white border border-[#e2e8f0] rounded-lg px-2.5 h-9 text-sm">
                <span className="text-slate-400 font-medium">Compare:</span>
                <select
                  value={compareWith}
                  onChange={(e) => setCompareWith(e.target.value as CompareMode)}
                  className="bg-transparent font-medium text-slate-800 focus:outline-none cursor-pointer"
                >
                  <option value="NONE">None</option>
                  <option value="PREV_QUARTER">Previous Quarter End</option>
                  <option value="PREV_FY_END">Previous FY End</option>
                </select>
              </div>
            </div>
          </div>

          {/* Top 4 Summary Cards */}
          {bs && (
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 print:hidden">
              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Assets</p>
                <p className="text-xl font-bold font-mono text-blue-700 mt-1">{formatINR(bs.assets.totalAssets)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Current + Fixed Assets</p>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Total Liabilities</p>
                <p className="text-xl font-bold font-mono text-amber-700 mt-1">{formatINR(bs.liabilities.totalLiabilities)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Current + Long Term Debt</p>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-5">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Net Worth (Equity)</p>
                <p className="text-xl font-bold font-mono text-emerald-700 mt-1">{formatINR(bs.equity.totalEquity)}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Capital + Retained Profits</p>
              </div>

              <div className="bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm flex flex-col justify-between">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Balance Check</p>
                <div className="mt-1">
                  <span
                    className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      bs.isBalanced
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        : 'bg-rose-50 text-rose-800 border border-rose-200'
                    }`}
                  >
                    {bs.isBalanced ? 'Balanced Statement ✅' : `Diff: ${formatINR(bs.difference)} ⚠️`}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Assets = Liabilities + Equity</p>
              </div>
            </div>
          )}

          {/* Top Financial Ratios Panel (MOVED TO TOP) */}
          {bs && bs.ratios && (
            <div className="w-full bg-white border border-[#e2e8f0] rounded-xl p-4 shadow-sm space-y-3 print:hidden">
              <div className="flex items-center justify-between pb-2 border-b border-[#e2e8f0]">
                <h3 className="text-xs font-bold text-[#1e293b] uppercase tracking-wider">Financial Solvency Ratios</h3>
                <span className="text-[11px] text-slate-500">Auto-calculated from Balance Sheet figures</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 text-xs">
                <div className="bg-slate-50 p-3 rounded-lg border border-[#e2e8f0]">
                  <p className="text-[10px] text-slate-500 font-medium uppercase">Current Ratio</p>
                  <p className="text-base font-bold font-mono text-[#1e293b] mt-0.5">{bs.ratios.currentRatio ?? 'N/A'}</p>
                  <p className="text-[10px] text-emerald-700 font-medium">Target: &gt; 1.5</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-[#e2e8f0]">
                  <p className="text-[10px] text-slate-500 font-medium uppercase">Quick Ratio</p>
                  <p className="text-base font-bold font-mono text-[#1e293b] mt-0.5">{bs.ratios.quickRatio ?? 'N/A'}</p>
                  <p className="text-[10px] text-emerald-700 font-medium">Target: &gt; 1.0</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-[#e2e8f0]">
                  <p className="text-[10px] text-slate-500 font-medium uppercase">Debt to Equity</p>
                  <p className="text-base font-bold font-mono text-[#1e293b] mt-0.5">{bs.ratios.debtToEquity ?? 'N/A'}</p>
                  <p className="text-[10px] text-emerald-700 font-medium">Target: &lt; 2.0</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-[#e2e8f0]">
                  <p className="text-[10px] text-slate-500 font-medium uppercase">Debt to Assets</p>
                  <p className="text-base font-bold font-mono text-[#1e293b] mt-0.5">{bs.ratios.debtToAssets ?? 'N/A'}</p>
                  <p className="text-[10px] text-emerald-700 font-medium">Target: &lt; 0.6</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-[#e2e8f0]">
                  <p className="text-[10px] text-slate-500 font-medium uppercase">Working Capital</p>
                  <p className="text-base font-bold font-mono text-[#1e293b] mt-0.5">{formatINR(bs.ratios.workingCapital)}</p>
                  <p className="text-[10px] text-slate-400">CA - CL</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-[#e2e8f0]">
                  <p className="text-[10px] text-slate-500 font-medium uppercase">Return on Assets</p>
                  <p className="text-base font-bold font-mono text-[#1e293b] mt-0.5">{bs.ratios.returnOnAssets}%</p>
                  <p className="text-[10px] text-slate-400">Net Profit / Assets</p>
                </div>
              </div>
            </div>
          )}

          {/* Main Statement Content (Two-Column Side-by-Side Layout) */}
          <div className="w-full">
            {loading ? (
              <div className="w-full bg-white border border-[#e2e8f0] rounded-xl py-16 text-center text-slate-400 flex flex-col items-center justify-center gap-2 shadow-sm">
                <i className="ri-loader-4-line animate-spin text-2xl text-primary-600" />
                <p className="text-xs font-medium text-slate-600">Computing Balance Sheet Statement...</p>
              </div>
            ) : !bs ? null : (
              <div className="w-full space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                  
                  {/* LEFT COLUMN: ASSETS */}
                  <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm flex flex-col justify-between">
                    <div>
                      <div className="px-4 py-3 bg-blue-50/70 border-b border-[#e2e8f0] flex items-center justify-between">
                        <h2 className="text-xs font-bold text-blue-900 uppercase tracking-wider">ASSETS</h2>
                        <span className="font-mono font-bold text-blue-700 text-sm">{formatINR(bs.assets.totalAssets)}</span>
                      </div>

                      <div className="p-4 space-y-4">
                        {/* Current Assets */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between pb-1 border-b border-[#e2e8f0] font-semibold text-slate-800 uppercase tracking-wider text-[11px]">
                            <span>1. Current Assets</span>
                            <span className="font-mono font-bold text-[#1e293b]">{formatINR(bs.assets.currentAssets.total)}</span>
                          </div>
                          <div className="space-y-1 pl-2">
                            {bs.assets.currentAssets.items.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => navigate(`/finance/ledger?accountId=${item.id}`)}
                                className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors text-xs group"
                              >
                                <span className="font-medium text-slate-800 group-hover:text-primary-600 transition-colors">
                                  {item.code} — {item.name}
                                </span>
                                <div className="flex items-center gap-3 font-mono">
                                  <span className="font-bold text-[#1e293b]">{formatINR(item.amount)}</span>
                                  {compareWith !== 'NONE' && (
                                    <span className="text-slate-500 font-normal">
                                      {formatINR(item.comparisonAmount || 0)}
                                    </span>
                                  )}
                                  {renderChangeBadge(item.changePercent)}
                                </div>
                              </div>
                            ))}
                            {bs.assets.currentAssets.items.length === 0 && (
                              <p className="text-slate-400 italic text-xs py-1 px-2">No current assets recorded</p>
                            )}
                          </div>
                        </div>

                        {/* Fixed Assets */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between pb-1 border-b border-[#e2e8f0] font-semibold text-slate-800 uppercase tracking-wider text-[11px]">
                            <span>2. Fixed & Non-Current Assets</span>
                            <span className="font-mono font-bold text-[#1e293b]">{formatINR(bs.assets.fixedAssets.total)}</span>
                          </div>
                          <div className="space-y-1 pl-2">
                            {bs.assets.fixedAssets.items.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => navigate(`/finance/ledger?accountId=${item.id}`)}
                                className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors text-xs group"
                              >
                                <span className="font-medium text-slate-800 group-hover:text-primary-600 transition-colors">
                                  {item.code} — {item.name}
                                </span>
                                <div className="flex items-center gap-3 font-mono">
                                  <span className="font-bold text-[#1e293b]">{formatINR(item.amount)}</span>
                                  {compareWith !== 'NONE' && (
                                    <span className="text-slate-500 font-normal">
                                      {formatINR(item.comparisonAmount || 0)}
                                    </span>
                                  )}
                                  {renderChangeBadge(item.changePercent)}
                                </div>
                              </div>
                            ))}
                            {bs.assets.fixedAssets.items.length === 0 && (
                              <p className="text-slate-400 italic text-xs py-1 px-2">No fixed assets recorded</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-3.5 bg-slate-50 border-t-2 border-blue-200 flex items-center justify-between font-bold text-xs">
                      <span className="text-blue-950 uppercase tracking-wider">TOTAL ASSETS</span>
                      <span className="font-mono text-blue-700 text-sm">{formatINR(bs.assets.totalAssets)}</span>
                    </div>
                  </div>

                  {/* RIGHT COLUMN: LIABILITIES & EQUITY */}
                  <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm flex flex-col justify-between">
                    <div>
                      <div className="px-4 py-3 bg-purple-50/70 border-b border-[#e2e8f0] flex items-center justify-between">
                        <h2 className="text-xs font-bold text-purple-900 uppercase tracking-wider">LIABILITIES & EQUITY</h2>
                        <span className="font-mono font-bold text-purple-700 text-sm">
                          {formatINR(bs.totalLiabilitiesAndEquity)}
                        </span>
                      </div>

                      <div className="p-4 space-y-4">
                        {/* Current Liabilities */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between pb-1 border-b border-[#e2e8f0] font-semibold text-slate-800 uppercase tracking-wider text-[11px]">
                            <span>1. Current Liabilities</span>
                            <span className="font-mono font-bold text-[#1e293b]">{formatINR(bs.liabilities.currentLiabilities.total)}</span>
                          </div>
                          <div className="space-y-1 pl-2">
                            {bs.liabilities.currentLiabilities.items.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => navigate(`/finance/ledger?accountId=${item.id}`)}
                                className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors text-xs group"
                              >
                                <span className="font-medium text-slate-800 group-hover:text-primary-600 transition-colors">
                                  {item.code} — {item.name}
                                </span>
                                <div className="flex items-center gap-3 font-mono">
                                  <span className="font-bold text-[#1e293b]">{formatINR(item.amount)}</span>
                                  {compareWith !== 'NONE' && (
                                    <span className="text-slate-500 font-normal">
                                      {formatINR(item.comparisonAmount || 0)}
                                    </span>
                                  )}
                                  {renderChangeBadge(item.changePercent)}
                                </div>
                              </div>
                            ))}
                            {bs.liabilities.currentLiabilities.items.length === 0 && (
                              <p className="text-slate-400 italic text-xs py-1 px-2">No current liabilities recorded</p>
                            )}
                          </div>
                        </div>

                        {/* Long-Term Liabilities */}
                        {bs.liabilities.longTermLiabilities.items.length > 0 && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between pb-1 border-b border-[#e2e8f0] font-semibold text-slate-800 uppercase tracking-wider text-[11px]">
                              <span>2. Long Term Liabilities</span>
                              <span className="font-mono font-bold text-[#1e293b]">{formatINR(bs.liabilities.longTermLiabilities.total)}</span>
                            </div>
                            <div className="space-y-1 pl-2">
                              {bs.liabilities.longTermLiabilities.items.map((item) => (
                                <div
                                  key={item.id}
                                  onClick={() => navigate(`/finance/ledger?accountId=${item.id}`)}
                                  className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors text-xs group"
                                >
                                  <span className="font-medium text-slate-800 group-hover:text-primary-600 transition-colors">
                                    {item.code} — {item.name}
                                  </span>
                                  <div className="flex items-center gap-3 font-mono">
                                    <span className="font-bold text-[#1e293b]">{formatINR(item.amount)}</span>
                                    {compareWith !== 'NONE' && (
                                      <span className="text-slate-500 font-normal">
                                        {formatINR(item.comparisonAmount || 0)}
                                      </span>
                                    )}
                                    {renderChangeBadge(item.changePercent)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Owner's Equity & Reserves */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between pb-1 border-b border-[#e2e8f0] font-semibold text-slate-800 uppercase tracking-wider text-[11px]">
                            <span>3. Equity & Capital Reserves</span>
                            <span className="font-mono font-bold text-[#1e293b]">{formatINR(bs.equity.totalEquity)}</span>
                          </div>
                          <div className="space-y-1 pl-2">
                            {bs.equity.items.map((item) => (
                              <div
                                key={item.id}
                                onClick={() => {
                                  if (item.id !== 'pnl-net-profit') navigate(`/finance/ledger?accountId=${item.id}`);
                                }}
                                className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors text-xs group"
                              >
                                <span className={`font-medium ${item.id === 'pnl-net-profit' ? 'font-semibold text-primary-700' : 'text-slate-800 group-hover:text-primary-600'}`}>
                                  {item.code} — {item.name}
                                </span>
                                <div className="flex items-center gap-3 font-mono">
                                  <span className="font-bold text-[#1e293b]">{formatINR(item.amount)}</span>
                                  {compareWith !== 'NONE' && (
                                    <span className="text-slate-500 font-normal">
                                      {formatINR(item.comparisonAmount || 0)}
                                    </span>
                                  )}
                                  {renderChangeBadge(item.changePercent)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-3.5 bg-slate-50 border-t-2 border-purple-200 flex items-center justify-between font-bold text-xs">
                      <span className="text-purple-950 uppercase tracking-wider">TOTAL LIABILITIES & EQUITY</span>
                      <span className="font-mono text-purple-700 text-sm">{formatINR(bs.totalLiabilitiesAndEquity)}</span>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>

        </div>
      </div>
    </AppLayout>
  );
}
